import moment, { Moment } from 'moment';
import { HttpClient } from '../httpClient';
import RecentTimesheet from '../models/recentTimesheetModel';
import SelectedTimesheet from '../models/SelectedTimesheetModel';
import StudentTimesheetEntry from '../models/studentTimesheetEntry';
import TimesheetEntryRequest from '../requests/timesheetEntryRequest';
import { DataResponse } from '../responses/baseResponse';
import TimesheetStatusesResponse from '../responses/timesheetStatusesResponse';
import TimesheetSubmitRequest from '../requests/timesheetSubmitRequest';
import TimesheetWeek from '../models/timesheetWeek';
import GroupedTimesheetEntry from '../models/groupedTimesheetEntry';
import TimesheetApiWeek from '../models/timesheetApiWeek';
import TimesheetSearchRequest from '../requests/timesheetSearchRequest';
import TimesheetSearch from '../models/timesheetSearch';
import { TimesheetSummarySearch } from '../models/timesheetSummarySearch';
import TimesheetWidgetModel from '../models/timesheetWidgetModel';
import { TimesheetEntrySummarySearch } from '../models/TimesheetEntrySummarySearch';
const httpClient = new HttpClient();

export const getTimesheetStatuses = async (
  disciplineId: number,
  startDate: Date,
  endDate: Date
) => {
  return httpClient.get<TimesheetStatusesResponse>({
    url: `/v1/timesheets/status?disciplineId=${disciplineId}&startDate=${startDate}&endDate=${endDate}&userId=${-1}`,
    requiresToken: true,
  });
};

export const getTimesheetStatusesUserId = async (
  disciplineId: number,
  startDate: Date,
  endDate: Date,
  numberOfMonths: number,
  userId: number,
) => {
  return httpClient.get<TimesheetStatusesResponse>({
    url: `/v1/timesheets/status?disciplineId=${disciplineId}&startDate=${startDate.toDateString()}&endDate=${endDate.toDateString()}&numberOfMonths=${numberOfMonths}&userId=${userId}`,
    requiresToken: true,
  });
};

export const getStudentsForEntry = async (
  disciplineId: number,
  schoolId: number,
  startOfweek: Moment,
  endOfWeek: Moment,
  isGroupEntry = false
) => {
  return httpClient.get<DataResponse<StudentTimesheetEntry[]>>({
    url: `/v1/timesheets/GetStudentsForEntry?disciplineId=${disciplineId}&schoolId=${schoolId}&startOfWeek=${startOfweek.format("YYYY-MM-DD HH:mm:ss")}&endOfWeek=${endOfWeek.format("YYYY-MM-DD HH:mm:ss")}&isGroupEntry=${isGroupEntry}`,
    requiresToken: true
  });
}

export const getEsyStudents = async (
  disciplineId: number,
  districtId: number,
  startOfweek: Moment,
  isGroupEntry = false
) => {
  return httpClient.get<DataResponse<StudentTimesheetEntry[]>>({
    url: `/v1/timesheets/GetEsyStudents?disciplineId=${disciplineId}&districtId=${districtId}&startOfWeek=${startOfweek.format("YYYY-MM-DD HH:mm:ss")}&isGroupEntry=${isGroupEntry}`,
    requiresToken: true
  });
}

export const getWeek = async (
  weekId: number,
) => {
  return httpClient.get<DataResponse<TimesheetApiWeek>>({
    url: `/v1/timesheets/Week?weekId=${weekId}`,
    requiresToken: true
  });
}


export const getRecentTimesheets = async () => {
  return httpClient.get<DataResponse<RecentTimesheet[]>>({
    url: 'v1/timesheets/RecentTimesheets',
    requiresToken: true
  });
}

export const getTimesheetById = async (timesheetId: number) => {
  return httpClient.get<DataResponse<SelectedTimesheet>>({
    url: `v1/timesheets/${timesheetId}`,
    requiresToken: true
  });
}

/**
 * Will create or update timesheet entries
 * Superseded by batchAddUpdateTimesheetEntries
 * @param entry
 * @returns the id of the timesheet created/updated
 */
export const addUpdateTimesheetEntry = async (entry: TimesheetEntryRequest) => {
  return httpClient.post<DataResponse<TimesheetWeek>>({
    url: '/v1/timesheets/AddUpdateTimesheetEntry',
    payload: entry,
    requiresToken: true,
  });
};


/**
 * This function is used when we need to add or update more than one entry at a time
 * This should be used instead of addUpdateTimesheetEntry
 * @param {TimesheetEntryRequest[]} entries - An array of timesheet task entries to add or update.
 * @return {Promise<DataResponse<TimesheetWeek>>} - A promise that resolves to the response data containing the updated timesheet week.
 */
export const batchAddUpdateTimesheetEntries = async (entries: TimesheetEntryRequest[]) => {
  return httpClient.post<DataResponse<TimesheetWeek[]>>({
    url: '/v1/timesheets/BatchAddUpdateTimesheetEntries',
    payload: entries,
    requiresToken: true,
  });
};

export const submitTimesheet = async (request: TimesheetSubmitRequest) => {
  return httpClient.put<DataResponse<string>>({
    url: '/v1/timesheets/UpdateStatus',
    payload: request,
    requiresToken: true,
  });
};

export const submitTimesheetApproved = async (request: TimesheetSubmitRequest) => {
  return httpClient.put<DataResponse<string>>({
    url: '/v1/timesheets/UpdateStatus',
    payload: request,
    requiresToken: true,
  });
};

export const getCurrentTimesheetStatusesByTherapist = async (therapistUserId: number) => {
  return httpClient.get<DataResponse<TimesheetWidgetModel[]>>({
    url: `/v1/timesheets/statusbyTherapist?userId=${therapistUserId}`,
    requiresToken: true,
  });
};

export const getTsFlagsByDirectorUserId = async () => {
  return httpClient.get<DataResponse<[]>>({
    url: `/v1/Directors/TSFlags`,
    requiresToken: true,
  });
};

export const getPendingTimesheetsByDirectorUserId = async () => {
  return httpClient.get<DataResponse<[]>>({
    url: `/v1/Directors/Timesheets`,
    requiresToken: true,
  });
};

export const getPendingInvoiceApprovals = async () => {
  return httpClient.get<DataResponse<[]>>({
    url: `/v1/Invoice/Pending`,
    requiresToken: true,
  });
};

export const getAdditionalEntries = async (startDate: Date, studentId: number, weekId: number, schoolId: number) => {
  return httpClient.get<DataResponse<GroupedTimesheetEntry[]>>({
    url: `/v1/Timesheets/Entries/Additional?StartDate=${moment(startDate).format("YYYY-MM-DD HH:mm:ss")}&studentId=${studentId}&weekId=${weekId}&schoolId=${schoolId}`,
    requiresToken: true,
  });
};

export const getTimesheets = async (
  request: TimesheetSearchRequest
) => {
  return httpClient.post<DataResponse<TimesheetSearch>>({
    url: `/v1/timesheets/GetTimesheets`,
    payload: request,
    requiresToken: true,
  });
};

export const searchTimesheetSummaries = async (therapistName: string = '', startDate: Date, endDate: Date, districtId: number = -1, schoolId: number = -1, disciplineId: number = -1, flagId: number = -1, divisionId: number = -1, statusId: number = -1, programId: number = -1) => {
  return httpClient.get<DataResponse<TimesheetSummarySearch[]>>({
    url: `/v1/Timesheets/SummarySearch?therapistName=${therapistName}&startDate=${startDate.toDateString()}&endDate=${endDate.toDateString()}&districtId=${districtId}&schoolId=${schoolId}&disciplineId=${disciplineId}&flagId=${flagId}&divisionId=${divisionId}&statusId=${statusId}&programId=${programId}`,
    requiresToken: true,
  });
};

export const searchTimesheetEntrySummaries = async (
  startDate: Date,
  endDate: Date,
  therapistId: number = -1,
  districtId: number = -1,
  schoolId: number = -1,
  disciplineId: number = -1,
  targetProgramId: number = -1,
  divisionId: number = -1,
  targetDisciplineId: number = -1,
  programId: number = -1,
  entryTypes: string[]
): Promise<{ data: TimesheetEntrySummarySearch[] | null; error: string | null }> => {
  try {
    const response = await httpClient.get<DataResponse<TimesheetEntrySummarySearch[]>>({
      url:
        `/v1/Timesheets/EntrySummarySearch?` +
        `startDate=${encodeURIComponent(startDate.toISOString())}` +
        `&endDate=${encodeURIComponent(endDate.toISOString())}` +
        `&therapistId=${therapistId}` +
        `&districtId=${districtId}` +
        `&schoolId=${schoolId}` +
        `&disciplineId=${disciplineId}` +
        `&targetProgramId=${targetProgramId}` +
        `&divisionId=${divisionId}` +
        `&targetDisciplineId=${targetDisciplineId}` +
        `&programId=${programId}` +
        entryTypes.map(et => `&entryTypes=${encodeURIComponent(et)}`).join(''),
      requiresToken: true,
    });

    return { data: response.data, error: null };
  } catch (err: unknown) {
    // Let httpClient.ts handle rejection message based on Message key
    return { data: null, error: err as string };
  }
};

export const checkGroupForRecoupment = async (studentIds: number[], startWeek: Date) => {
  return httpClient.get<DataResponse<boolean>>({
    url: `/v1/timesheets/CheckGroupForRecoupment?startOfWeek=${moment(startWeek).format("YYYY-MM-DD HH:mm:ss")}&studentIds=${studentIds.join(`&studentIds=`)}`,
    requiresToken: true
  });
}
export const batchTransferDisciplinePrograms = async (
  request: any[],
  therapistId: number = -1,
  discipline: number = -1,
  programId: number = -1,             
  targetDisciplineId: number = -1,   
  targetProgramId: number = -1       
) => {
  const url = `/v1/Timesheets/BatchTransferDisciplinePrograms?` +
  `currentTherapistUserId=${encodeURIComponent(therapistId)}` +
  `&currentDisciplineId=${encodeURIComponent(discipline)}` +
  `&currentProgramId=${encodeURIComponent(programId)}` +
  `&TargetDisciplineId=${encodeURIComponent(targetDisciplineId)}` +
  `&TargetProgramId=${encodeURIComponent(targetProgramId)}`;
  return httpClient.put<DataResponse<{message: string, success: boolean}>>({
    url: url,
    payload: request,
    requiresToken: true
  });
};